import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, MapPin } from "lucide-react";
import { KineticLines, Reveal, Eyebrow } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";

const CHAPTERS = [
  {
    num: "01",
    title: "Data-Driven",
    body: "High-performing B2B growth does not come from high-volume spam — it stems from actionable market intelligence delivered to the exact individuals who hold decision-making power.",
  },
  {
    num: "02",
    title: "Revenue-Focused",
    body: "Today's B2B buying committees average 6 to 12 stakeholders, each with unique technical requirements, budget priorities, and organizational incentives. Reaching them demands contextual relevance, verified consent, and persistent multi-channel engagement.",
  },
  {
    num: "03",
    title: "People-Powered",
    body: "We bridge the gap between technology creators and technology adopters with full-funnel media solutions backed by an audited database of 140M+ B2B decision-makers worldwide — verified by humans, not just machines.",
  },
];

const VALUES = [
  { t: "Absolute Integrity", s: "Uncompromising Data Authenticity", d: "We never cut corners or sacrifice data quality for volume. Every lead delivered is authentic, fully opted-in, and rigorously validated." },
  { t: "Partner Mentality", s: "Client-Centric Execution", d: "We treat your budget and growth targets as our own — open communication, real-time reporting, and an unwavering commitment to meeting campaign allocations." },
  { t: "Adaptive Agility", s: "Continuous Innovation", d: "B2B buyer behavior is constantly evolving. We continually refine our targeting models, intent signals, and syndication platforms to stay ahead of market shifts." },
  { t: "Ethical Compliance", s: "Global Regulatory Stewardship", d: "We champion privacy and regulatory standards, ensuring full compliance across GDPR, CCPA, CASL, and international privacy mandates." },
];

const COMPARE = [
  { dim: "Data Sourcing", legacy: "Scraped, outdated directories and shared third-party files.", vy: "Proprietary opt-in research hubs, first-party digital communities, and active syndication networks." },
  { dim: "Verification Engine", legacy: "Basic email regex syntax checks only.", vy: "Triple-layer validation: syntax verification, digital telemetry tracking, and manual tele-verification." },
  { dim: "Lead Quality Accountability", legacy: "Strict 'as-is' sales with high rejection rates and difficult refund policies.", vy: "100% Quality & Replacement Guarantee — any lead failing agreed ICP criteria is replaced promptly." },
  { dim: "Global Reach & Localization", legacy: "Limited to English-speaking regions with patchy regional accuracy.", vy: "Active delivery across 90+ markets in NAM, EMEA, APAC, and LATAM with multilingual support." },
  { dim: "Compliance Framework", legacy: "Ambiguous consent trails; high legal liability risk.", vy: "Documented opt-in timestamps, URL attribution, and complete GDPR/CCPA compliance audit trails." },
];

const HUBS = [
  { city: "Wilmington, Delaware", role: "Americas Hub", addr: "1201 N Orange St, Suite 700, DE 19801, United States" },
  { city: "London", role: "European Hub", addr: "25 Finsbury Circus, London EC2M 7EE, United Kingdom" },
  { city: "Pune & Bengaluru", role: "APAC Tech Center", addr: "Cyber City, Magarpatta / Hinjawadi, Pune 411028, India" },
];

export default function About() {
  useEffect(() => {
    document.title = "About Vyacomm Media | Trusted B2B Demand Generation & Data Partner";
  }, []);

  return (
    <div data-testid="about-page">
      <section className="grain hero-grid-lines relative overflow-hidden bg-navy pb-24 pt-[200px]" data-testid="about-hero">
        <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-cobalt/20 blur-[140px]" />
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal delay={0.05} y={16}>
            <Eyebrow dark>About Vyacomm Media</Eyebrow>
          </Reveal>
          <h1 className="mt-6 max-w-4xl font-jakarta text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <KineticLines lines={["Where Data Precision Meets", "B2B Pipeline Velocity."]} accentIndex={1} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              We are more than a lead generation vendor. We are the strategic pipeline acceleration engine behind the
              world's most ambitious B2B technology enterprises and media agencies.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee dark={false} />

      {/* MANIFESTO CHAPTERS */}
      <section className="bg-canvas py-24 sm:py-32" data-testid="manifesto-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Our Core Philosophy</Eyebrow>
          </Reveal>
          <div className="mt-10 space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div className="group grid gap-4 border-t border-line py-12 transition-colors duration-500 hover:bg-white sm:grid-cols-12 sm:gap-8 sm:px-6">
                  <span className="font-jakarta text-6xl font-extrabold text-line transition-colors duration-500 group-hover:text-cobalt sm:col-span-2 sm:text-7xl">
                    {c.num}
                  </span>
                  <h2 className="self-center font-jakarta text-2xl font-extrabold tracking-tight text-navy sm:col-span-3 sm:text-3xl">
                    {c.title}
                  </h2>
                  <p className="self-center text-base leading-relaxed text-slate-600 sm:col-span-7">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="story-section">
        <div className="relative z-10 mx-auto grid max-w-[1808px] gap-12 px-5 sm:px-8 lg:px-12 2xl:px-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow dark>Our Story & Evolution</Eyebrow>
            <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Founded to fix everything broken about lead brokers
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="space-y-5 text-base leading-relaxed text-slate-300">
              <p>
                Vyacomm Media was founded by a collective of veteran B2B marketing technologists, data scientists, and
                demand generation strategists who witnessed the systemic flaws of traditional lead brokers: bloated
                lists, fake contact records, missed delivery deadlines, and zero attribution transparency.
              </p>
              <p>
                We set out to build an agency that puts client ROI and lead authenticity first. By combining proprietary
                technographic intelligence, deep audience syndication hubs, and a relentless commitment to human-led
                lead verification, Vyacomm Media rapidly grew from a boutique data specialist into a premier global
                demand generation publisher operating in more than 90 international markets.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white py-24 sm:py-32" data-testid="values-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Core Values</Eyebrow>
            <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              The operating principles behind every campaign
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-line bg-canvas p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cobalt/50">
                  <span className="font-mono2 text-xs tracking-[0.2em] text-flame">0{i + 1}</span>
                  <h3 className="mt-3 font-jakarta text-lg font-bold text-navy">{v.t}</h3>
                  <p className="mt-0.5 font-mono2 text-[10px] tracking-[0.16em] text-cobalt">{v.s.toUpperCase()}</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-canvas py-24 sm:py-32" data-testid="comparison-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>The Vyacomm Difference</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Why we outperform legacy publishers
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="mt-12">
            <div className="overflow-hidden rounded-2xl border border-line bg-white" data-testid="comparison-table">
              <div className="hidden grid-cols-12 border-b border-line bg-navy px-6 py-4 sm:grid">
                <span className="col-span-3 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">BENCHMARK DIMENSION</span>
                <span className="col-span-4 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">LEGACY LEAD BROKERS</span>
                <span className="col-span-5 font-mono2 text-[10px] tracking-[0.22em] text-flame">THE VYACOMM STANDARD</span>
              </div>
              {COMPARE.map((row) => (
                <div key={row.dim} className="grid gap-4 border-b border-line px-6 py-6 last:border-b-0 sm:grid-cols-12 sm:gap-6">
                  <h3 className="col-span-3 font-jakarta text-sm font-bold text-navy">{row.dim}</h3>
                  <p className="col-span-4 flex items-start gap-2 text-sm leading-relaxed text-slate-500">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                    {row.legacy}
                  </p>
                  <p className="col-span-5 flex items-start gap-2 text-sm leading-relaxed text-charcoal">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    {row.vy}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* HUBS + CTA */}
      <section className="grain relative overflow-hidden bg-navy py-24" data-testid="about-hubs">
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow dark>Global Operating Hubs</Eyebrow>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {HUBS.map((h, i) => (
              <Reveal key={h.city} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                  <MapPin className="h-5 w-5 text-flame" />
                  <p className="mt-4 font-mono2 text-[10px] tracking-[0.22em] text-cobaltlight">{h.role.toUpperCase()}</p>
                  <h3 className="mt-1 font-jakarta text-xl font-bold text-white">{h.city}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">{h.addr}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl border border-cobalt/30 bg-eblue/40 p-8 sm:flex-row sm:items-center">
              <h3 className="max-w-xl font-jakarta text-2xl font-extrabold text-white">
                Partner with the publisher that treats your pipeline as its own.
              </h3>
              <Link
                to="/contact-us"
                data-testid="about-cta-btn"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-cobalt px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:bg-[#1d4fd7]"
              >
                Book a Strategy Session
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
